import { PlusOutlined } from "@ant-design/icons";
import { Alert, Button } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FromToFilter, useErrorHistory } from "../../../api";
import { useSCBOneHistoryAlerts } from "../../../api/api.scb-one";
import "../../../styles/card.scss";
import { BaseCard } from "../../BaseCard";
import { BeaconAlertDrawer, SCBOneAlertDrawer } from "./AlertDrawer";
import { AlertTable } from "./AlertTable";

interface SCBOneAlertCardProps {
  serial: string;
  selectedFromToFilter: {
    from: Date | null;
    to: Date | null;
  };
  operationRunSelected: boolean;
}

export function SCBOneAlertCard({
  serial,
  selectedFromToFilter,
  operationRunSelected,
}: SCBOneAlertCardProps) {
  const { t } = useTranslation();
  const [alertDrawerVisible, setAlertDrawerVisible] = useState(false);

  const validSelectedFromToFilter = useMemo(() => {
    return {
      from: selectedFromToFilter.from as Date,
      to: selectedFromToFilter.to ?? new Date(),
    };
  }, [selectedFromToFilter]);

  return (
    <BaseCard title={t("beacon:error-details")} allowScroll={true}>
      {operationRunSelected ? (
        <SCBOneAlertContent
          serial={serial}
          selectedFromToFilter={validSelectedFromToFilter}
          alertDrawerVisible={alertDrawerVisible}
          operationRunSelected={operationRunSelected}
          setAlertDrawerVisible={setAlertDrawerVisible}
        />
      ) : (
        <>
          <Alert
            message={t("scb-one:run-notification-select")}
            type="info"
            showIcon
          />
          <SCBOneAlertDrawer
            alertHistory={undefined}
            isLoading={false}
            operationRunSelected={operationRunSelected}
            alertDrawerVisible={alertDrawerVisible}
            onCloseDrawer={() => setAlertDrawerVisible(false)}
          />
          <Button
            aria-label="show-drawer-button"
            className="show-card-drawer-button"
            type="primary"
            onClick={() => setAlertDrawerVisible(true)}
            icon={<PlusOutlined />}
          />
        </>
      )}
    </BaseCard>
  );
}

interface AlertCardContentProps {
  serial: string;
  selectedFromToFilter: {
    from: Date;
    to: Date;
  };
  alertDrawerVisible: boolean;
  operationRunSelected: boolean;
  setAlertDrawerVisible: (visible: boolean) => void;
}

function SCBOneAlertContent({
  serial,
  selectedFromToFilter,
  alertDrawerVisible,
  operationRunSelected,
  setAlertDrawerVisible,
}: AlertCardContentProps) {
  const { t } = useTranslation();

  const alertHistory = useSCBOneHistoryAlerts(serial, selectedFromToFilter);
  return (
    <>
      <AlertTable
        alertHistory={alertHistory}
        isLoading={alertHistory.loading}
      />
      <SCBOneAlertDrawer
        alertHistory={alertHistory ?? []}
        isLoading={alertHistory.loading}
        operationRunSelected={operationRunSelected}
        alertDrawerVisible={alertDrawerVisible}
        onCloseDrawer={() => setAlertDrawerVisible(false)}
      />
      <Button
        aria-label="show-drawer-button"
        className="show-card-drawer-button"
        type="primary"
        onClick={() => setAlertDrawerVisible(true)}
        icon={<PlusOutlined />}
      />
    </>
  );
}

interface BeaconAlertCardProps {
  serial: string;
  selectedFromToFilter:
    | (FromToFilter & number)
    | {
        from: Date;
        to: Date;
      };
}

export function BeaconAlertCard({
  serial,
  selectedFromToFilter,
}: BeaconAlertCardProps) {
  const [alertDrawerVisible, setAlertDrawerVisible] = useState(false);
  const { t } = useTranslation();
  const alertHistory = useErrorHistory(serial, selectedFromToFilter);

  return (
    <BaseCard title={t("beacon:error-details")} allowScroll={true}>
      <AlertTable
        isLoading={alertHistory.loading}
        alertHistory={alertHistory ?? []}
      />
      <BeaconAlertDrawer
        serial={serial}
        alertHistory={alertHistory ?? []}
        alertDrawerVisible={alertDrawerVisible}
        isLoading={alertHistory.loading}
        onCloseDrawer={() => setAlertDrawerVisible(false)}
      />
      <Button
        aria-label="show-drawer-button"
        className="show-card-drawer-button"
        type="primary"
        onClick={() => setAlertDrawerVisible(true)}
        icon={<PlusOutlined />}
      ></Button>
    </BaseCard>
  );
}
